import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Link as LinkIcon, FileText, StickyNote, Upload, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TABS = [
  { id: "link", label: "Link", icon: LinkIcon },
  { id: "note", label: "Note", icon: StickyNote },
  { id: "file", label: "File", icon: Upload },
];

export default function AddSourceModal({ open, onClose, onAddLink, onAddNote, onUploadFile }) {
  const [tab, setTab] = useState("link");
  const [url, setUrl] = useState("");
  const [noteText, setNoteText] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setUrl("");
    setNoteText("");
    setFile(null);
    setSubmitting(false);
  };

  const getTitle = () => {
    if (tab === "link") return url.trim() || "Link";
    if (tab === "note") return (noteText.trim().split("\n")[0] || "Untitled note").slice(0, 100);
    if (tab === "file" && file) return file.name;
    return "Untitled";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const title = getTitle();
    if (!title) return;
    setSubmitting(true);
    try {
      if (tab === "link") {
        await onAddLink({ title, url: url.trim() });
      } else if (tab === "note") {
        await onAddNote({ title, content: noteText.trim() });
      } else if (tab === "file" && file) {
        await onUploadFile({ title, file });
      }
      handleClose();
    } catch {
      setSubmitting(false);
    }
  };

  const canSubmit = (
    (tab === "link" && url.trim()) ||
    (tab === "note" && noteText.trim()) ||
    (tab === "file" && file)
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/10 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 8 }}
        className="surface-elevated rounded-2xl p-6 w-full max-w-lg mx-4 shadow-xl"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-foreground text-base">Add Source</h2>
          <Button variant="ghost" className="rounded-full h-8 w-8 p-0" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 p-1 bg-secondary/60 rounded-xl">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                type="button"
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-medium transition-all ${
                  tab === t.id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="space-y-4">
          {tab === "link" && (
            <div>
              <label className="text-xs text-muted-foreground font-medium mb-1.5 block">URL</label>
              <input
                value={url}
                onChange={e => setUrl(e.target.value)}
                className="surface-input w-full h-10 px-4 rounded-xl text-sm text-foreground"
                placeholder="https://..."
                autoFocus
              />
            </div>
          )}

          {tab === "note" && (
            <div>
              <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Content</label>
              <textarea
                autoFocus
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                className="surface-input w-full px-4 py-3 rounded-xl text-sm text-foreground resize-none"
                rows={5}
                placeholder="Paste your notes, talking points, or key information..."
              />
            </div>
          )}

          {tab === "file" && (
            <div>
              <label className="text-xs text-muted-foreground font-medium mb-1.5 block">File</label>
              <div className="surface-input rounded-xl p-4 text-center">
                {file ? (
                  <div className="flex items-center gap-2 justify-center">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{file.name}</span>
                    <button type="button" onClick={() => setFile(null)} className="p-1 rounded hover:bg-secondary">
                      <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2 py-2">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Click to upload (PDF, DOCX, TXT)</span>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt,.md,.csv"
                      onChange={e => setFile(e.target.files?.[0] || null)}
                    />
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={handleClose} className="rounded-full h-9 text-sm">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="rounded-full h-9 text-sm px-5 gap-1.5"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Add Source
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
